import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface User {
  id: number;
  email: string;
  full_name: string;
  is_advanced_user: boolean;
  is_expert: boolean;
  is_superuser: boolean;
  is_secret_corner?: boolean;
}

interface ProfessionalHeaderProps {
  className?: string;
  user?: User | null;
  onLogout?: () => void;
}

const ProfessionalHeader: React.FC<ProfessionalHeaderProps> = ({ className = "", user, onLogout }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isExpertRoute = location.pathname.startsWith('/expert');
  const isExpertUser = user?.is_expert || user?.is_superuser;
  const roleLabel = user?.is_superuser ? 'Admin' : user?.is_expert ? 'Expert' : user?.is_advanced_user ? 'Advanced' : 'Basic';

  const closeMobileMenu = () => setShowMobileMenu(false);
  const closeUserMenu = () => setShowUserMenu(false);

  // Player's Secret Corner: 5 logo clicks within 2s routes a pass holder (or superuser) to
  // /players. See imp_mds/PLAYERS_SECRET_CORNER_PLAN.md §3. Every click still navigates home
  // as before — the 5th click just overrides the destination in the same handler, so there's
  // no behavior change for users without the pass or for a normal single click.
  const logoClickCountRef = useRef(0);
  const logoClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (logoClickTimerRef.current) clearTimeout(logoClickTimerRef.current);
    };
  }, []);

  const handleLogoClick = () => {
    navigate('/');
    if (!user?.is_superuser && !user?.is_secret_corner) return;

    logoClickCountRef.current += 1;
    if (logoClickTimerRef.current) clearTimeout(logoClickTimerRef.current);

    if (logoClickCountRef.current >= 5) {
      logoClickCountRef.current = 0;
      navigate('/players');
      return;
    }

    logoClickTimerRef.current = setTimeout(() => {
      logoClickCountRef.current = 0;
    }, 2000);
  };

  return (
    <>
      <header className={`text-white relative z-50 transition-all duration-300 ${className} ${scrolled ? 'py-1' : 'py-2 sm:py-3'}`} style={{backgroundColor: 'var(--slate)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between relative">

            {/* Left: robot logo (desktop, shrinks on scroll) + invisible spacer (mobile) */}
            <div className="flex-shrink-0 cursor-pointer" onClick={handleLogoClick}>
              <img
                src="/dig_no_name_logo_cropped.png"
                alt="dialledin.golf"
                className={`object-contain transition-all duration-300 hidden sm:block ${
                  scrolled ? 'w-10 h-10' : 'w-16 h-16'
                }`}
              />
              {/* Spacer matches hamburger/sign-in width so centered logo stays centered */}
              <div className="sm:hidden w-11 h-11" />
            </div>

            {/* Mobile: robot logo absolutely centered */}
            <div
              className="sm:hidden absolute left-1/2 -translate-x-1/2 cursor-pointer"
              onClick={handleLogoClick}
            >
              <img
                src="/dig_no_name_logo_cropped.png"
                alt="dialledin.golf"
                className="w-14 h-14 object-contain"
              />
            </div>

            {/* Desktop: name absolutely centered (hidden for expert users on md+ where nav takes center) */}
            {!(user && isExpertUser) && (
              <div
                className="hidden sm:block absolute left-1/2 -translate-x-1/2 cursor-pointer transition-all duration-300"
                onClick={handleLogoClick}
              >
                <img
                  src="/dig_name_only_light_ink.png"
                  alt="dialledin.golf"
                  className={`w-auto object-contain transition-all duration-300 ${
                    scrolled ? 'h-6' : 'h-10'
                  }`}
                />
              </div>
            )}

            {/* Center: Expert Nav — desktop only */}
            {user && isExpertUser && (
              <nav className="hidden md:flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    !isExpertRoute ? 'bg-green-ink text-white' : 'text-white hover:opacity-90'
                  }`}
                >
                  Recommendations
                </button>
                <button
                  onClick={() => navigate('/expert')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isExpertRoute ? 'bg-blue-600 text-white' : 'text-white hover:opacity-90'
                  }`}
                >
                  Fitter's Corner
                </button>
              </nav>
            )}

{/* Right side */}
            <div className="flex items-center gap-2">
              {/* Sign In (unauthenticated) */}
              {!user && (
                <button
                  onClick={() => navigate('/login')}
                  className="px-5 py-2 bg-white text-green-ink font-semibold rounded-lg hover:bg-green/10 transition-colors"
                >
                  Sign In
                </button>
              )}

              {/* Desktop User Menu */}
              {user && onLogout && (
                <div className="relative hidden sm:block">
                  <button
                    onClick={() => { setShowUserMenu(!showUserMenu); closeMobileMenu(); }}
                    className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:opacity-90 transition-colors"
                  >
                    <div className="hidden sm:block text-right">
                      <p className="font-medium text-white">{user.full_name}</p>
                      <p className="text-sm" style={{color: 'var(--steel)'}}>{roleLabel}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-green-ink flex items-center justify-center text-white font-bold border-2 border-green-ink">
                      {user.full_name.charAt(0).toUpperCase()}
                    </div>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <div className="px-4 py-2">
                        <p className="text-xs text-gray-500 mb-1">Access Level:</p>
                        <div className="flex flex-wrap gap-1">
                          {user.is_superuser && <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">Admin</span>}
                          {user.is_expert && <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Expert</span>}
                          {user.is_advanced_user && <span className="text-xs px-2 py-1 bg-green/15 text-green-ink rounded">Advanced</span>}
                          {!user.is_superuser && !user.is_expert && !user.is_advanced_user && (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">Basic</span>
                          )}
                        </div>
                      </div>
                      <div className="border-t border-gray-200 my-2"></div>
                      <button
                        onClick={() => { closeUserMenu(); navigate('/dashboard'); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        My Fittings
                      </button>
                      {user.is_superuser && (
                        <button
                          onClick={() => { closeUserMenu(); navigate('/admin/users'); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          User access
                        </button>
                      )}
                      {user.is_superuser && (
                        <button
                          onClick={() => { closeUserMenu(); navigate('/bag'); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          My Bag
                        </button>
                      )}
                      {user.is_superuser && (
                        <button
                          onClick={() => { closeUserMenu(); navigate('/bag?view=map&focus=bends'); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h6" />
                          </svg>
                          Loft &amp; gapping check
                        </button>
                      )}
                      <button
                        onClick={() => { closeUserMenu(); onLogout(); }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Hamburger — mobile only, authenticated */}
              {user && (
                <button
                  onClick={() => { setShowMobileMenu(!showMobileMenu); closeUserMenu(); }}
                  className="sm:hidden flex items-center justify-center w-11 h-11 rounded-lg hover:opacity-90 transition-colors"
                  aria-label="Toggle menu"
                >
                  {showMobileMenu ? (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Drawer */}
        {user && showMobileMenu && (
          <div
            className="sm:hidden"
            style={{backgroundColor: 'var(--ink)', borderTop: '1px solid rgba(192,194,195,0.2)'}}
          >
            {/* User identity strip */}
            <div className="px-4 py-4 flex items-center gap-3" style={{borderBottom: '1px solid rgba(184,212,168,0.15)'}}>
              <div className="w-11 h-11 rounded-full bg-green-ink flex items-center justify-center text-white font-bold text-lg border-2 border-green-ink flex-shrink-0">
                {user.full_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-white">{user.full_name}</p>
                <p className="text-sm" style={{color: 'var(--steel)'}}>{roleLabel}</p>
              </div>
            </div>

            {/* Expert nav links */}
            {isExpertUser && (
              <div style={{borderBottom: '1px solid rgba(184,212,168,0.15)'}}>
                <button
                  onClick={() => { closeMobileMenu(); navigate('/'); }}
                  className={`w-full text-left px-4 py-4 text-base font-medium transition-colors flex items-center gap-3 ${
                    !isExpertRoute ? 'text-white bg-green-ink' : 'text-white hover:opacity-90'
                  }`}
                  style={{minHeight: '44px'}}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Recommendations
                </button>
                <button
                  onClick={() => { closeMobileMenu(); navigate('/expert'); }}
                  className={`w-full text-left px-4 py-4 text-base font-medium transition-colors flex items-center gap-3 ${
                    isExpertRoute ? 'text-white bg-blue-700' : 'text-white hover:opacity-90'
                  }`}
                  style={{minHeight: '44px'}}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Fitter's Corner
                </button>
              </div>
            )}

            {/* Actions */}
            {onLogout && (
              <div>
                <button
                  onClick={() => { closeMobileMenu(); navigate('/dashboard'); }}
                  className="w-full text-left px-4 py-4 text-base text-white hover:opacity-90 transition-colors flex items-center gap-3"
                  style={{minHeight: '44px', borderBottom: '1px solid rgba(184,212,168,0.1)'}}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  My Fittings
                </button>
                {user.is_superuser && (
                  <button
                    onClick={() => { closeMobileMenu(); navigate('/admin/users'); }}
                    className="w-full text-left px-4 py-4 text-base text-white hover:opacity-90 transition-colors flex items-center gap-3"
                    style={{minHeight: '44px', borderBottom: '1px solid rgba(184,212,168,0.1)'}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    User access
                  </button>
                )}
                {user.is_superuser && (
                  <button
                    onClick={() => { closeMobileMenu(); navigate('/bag'); }}
                    className="w-full text-left px-4 py-4 text-base text-white hover:opacity-90 transition-colors flex items-center gap-3"
                    style={{minHeight: '44px', borderBottom: '1px solid rgba(184,212,168,0.1)'}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    My Bag
                  </button>
                )}
                {user.is_superuser && (
                  <button
                    onClick={() => { closeMobileMenu(); navigate('/bag?view=map&focus=bends'); }}
                    className="w-full text-left px-4 py-4 text-base text-white hover:opacity-90 transition-colors flex items-center gap-3"
                    style={{minHeight: '44px', borderBottom: '1px solid rgba(184,212,168,0.1)'}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h6" />
                    </svg>
                    Loft &amp; gapping check
                  </button>
                )}
                <button
                  onClick={() => { closeMobileMenu(); onLogout(); }}
                  className="w-full text-left px-4 py-4 text-base font-medium transition-colors flex items-center gap-3"
                  style={{minHeight: '44px', color: '#fca5a5'}}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Click-outside overlay — covers both menus */}
      {(showUserMenu || showMobileMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { closeUserMenu(); closeMobileMenu(); }}
        />
      )}
    </>
  );
};

export default ProfessionalHeader;